"use client";

import { Account } from "@aptos-labs/ts-sdk";
import { QrCode } from "@ark-ui/react/qr-code";
import { IconLock } from "@decibeltrade/react-icons";
import { Button } from "@decibeltrade/web-core/components";
import { ModalContent, ModalRenderProps, openModal } from "@decibeltrade/web-core/modal";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useRef, useState } from "react";
import { css } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { getSdkConfig } from "@/contexts/dex-sdk";
import { useAddDelegation } from "@/contexts/mutations/delegation/add-delegation";
import { useWriteDexSdk } from "@/contexts/write-dex-sdk-context";
import { useAuthState } from "@/hooks/use-auth-state";
import { useHoldToProgress } from "@/hooks/use-hold-to-progress";
import { useSubaccountsWithPrimary } from "@/hooks/use-subaccounts-with-primary";
import { addressesEqual } from "@/utils/address";
import { Logger } from "@/utils/logger";

import { CircularLoader } from "../animated/circular-loader";
import { InfoBlock } from "../info-block";
import { toast } from "../toast";
import { DecibelFavicon } from "../vectors/decibel-favicon";
import { ModalCloseButton } from "./modal-close-button";

export const openLinkMobileModal = () => {
  openModal({
    id: "link-mobile",
    renderContent: (modalProps) => <LinkMobileModal {...modalProps} />,
  });
};

const MOBILE_QR_DELEGATION_NAME = "Mobile QR";

const LinkMobileModal = ({ close }: ModalRenderProps) => {
  const t = useTranslations("linkMobileModal");
  const tGeneral = useTranslations("general");
  const {
    addDelegation: { mutate, isPending: isDelegationPending },
    isPendingSignature,
  } = useAddDelegation();
  const { subaccountAddress } = useWriteDexSdk();
  const { accountAddress, ownerWalletAddress } = useAuthState();
  const { subaccounts } = useSubaccountsWithPrimary();

  const selectedSubaccount = subaccounts.find((subaccount) =>
    addressesEqual(subaccount.subaccount_address, subaccountAddress),
  );

  const hasRunOnce = useRef<boolean>(false);

  const account = useMemo(() => Account.generate(), []);

  const [_isRevealed, setIsRevealed] = useState(false);

  const { progress, eventHandlers } = useHoldToProgress({
    msTime: 2_000,
    onComplete: () => setIsRevealed(true),
  });

  // Send tx to delegate trading to for subaccount
  useEffect(() => {
    if (hasRunOnce.current) return;

    hasRunOnce.current = true;

    // @Todo: Fix this. If not in setTimeout, mutation state is not updated correctly.
    setTimeout(() => {
      mutate(
        { account, name: MOBILE_QR_DELEGATION_NAME, isMobileQr: true },
        {
          onError: (error) => {
            toast({
              title: tGeneral("error"),
              description: tGeneral("unexpectedError"),
              variant: "error",
            });
            Logger.error(error, {
              message: "Failed to delegate trading to for subaccount",
              accountAddress: account.accountAddress.toString(),
              name: MOBILE_QR_DELEGATION_NAME,
            });
            close();
          },
        },
      );
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shareUrl = useMemo(() => {
    if (!subaccountAddress || !accountAddress) return undefined;
    return JSON.stringify({
      privateKey: account.privateKey.toAIP80String(),
      subaccountAddress,
      ownerAddress: ownerWalletAddress,
      accountAddress,
      label: selectedSubaccount?.custom_label,
      deploymentPackage: getSdkConfig().deployment.package,
    });
  }, [
    account,
    subaccountAddress,
    accountAddress,
    selectedSubaccount?.custom_label,
    ownerWalletAddress,
  ]);

  // Subaccount and currentAccount should always be available by this point
  if (!subaccountAddress || !accountAddress) {
    close();
    return null;
  }

  // The user must press and hold the button for 2 seconds to reveal the QR code.
  // During this time, we attempt to complete the delegation request.
  // If the delegation is not completed within that window, the QR code remains hidden and a loader is shown on the button.
  // The loader will continue to display until the user finishes signing
  const loading = isPendingSignature || (_isRevealed ? isDelegationPending : false);
  const isRevealed = _isRevealed && !isDelegationPending;

  return (
    <ModalContent className={modalContentStyles}>
      <ModalCloseButton close={close} />

      <DecibelFavicon
        className={css({ alignSelf: "center", color: "brandYellow.500", h: "[94px]", w: "[94px]" })}
      />

      <h2 className={titleStyles}>{t("title")}</h2>

      <p className={descriptionStyles}>{t("instructions")}</p>

      <div className={qrCodeContainerStyles}>
        <QrCode.Root
          className={qrCodeStyles}
          style={{ filter: isRevealed ? "none" : "blur(10px)" }}
          value={shareUrl}
        >
          <QrCode.Frame>
            <QrCode.Pattern />
          </QrCode.Frame>
        </QrCode.Root>

        {!isRevealed && (
          <CircularLoader
            progress={progress}
            size={42}
            thickness={6}
            className={flex({ color: "brandYellow.500", position: "absolute" })}
          >
            <IconLock className={css({ h: "14", w: "14", m: "14", position: "absolute" })} />
          </CircularLoader>
        )}
      </div>

      <Button
        fullWidth
        variant="primary"
        loading={loading}
        disabled={loading || isRevealed}
        {...eventHandlers}
        tabIndex={0}
      >
        {t("holdToReveal")}
      </Button>

      <InfoBlock>{t("securityWarning")}</InfoBlock>
    </ModalContent>
  );
};

const modalContentStyles = stack({
  position: "relative",
  w: "[529px]",
  bg: "background.canvas",
  borderRadius: "300",
  display: "inline-flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  p: "24",
  gap: "20",
  textAlign: "center",
});

const titleStyles = css({
  textStyle: "heading.xs",
  color: "text.primary",
});

const descriptionStyles = css({
  textStyle: "highlight.md",
  color: "text.secondary",
});

const qrCodeContainerStyles = flex({
  position: "relative",
  w: "full",
  maxW: "[273]",
  aspectRatio: "1",
  alignSelf: "center",
  alignItems: "center",
  justifyContent: "center",
});

const qrCodeStyles = css({
  h: "full",
  w: "full",
  p: "12",
  borderRadius: "400",
  bg: "text.primary",
});
